// components/GetStartedModular.tsx
import React, { useState, useEffect, useContext, Children, useCallback } from "react";
import QuestContext from "../../../../components/QuestWrapper";
import { GetStartedContext } from "./GetStartedContext";
import Loader from "../../../../components/Login/Loader";
import GetStartedApi from "../components/getStartedApis";
import ThemeManager from "../components/themeManager";
import General, { useGeneral } from "../../../../general";
import "../components/getStarted.css";
import { GetStartedProps, TutorialStep } from "./types";
import Header from "./Header";
import Progress from "./Progress";
import Content from "./Content";
import Footer from "./Footer";

const GetStarted: React.FC<GetStartedProps & { children?: React.ReactNode }> & {
  Header: typeof Header;
  Progress: typeof Progress;
  Content: typeof Content;
  Footer: typeof Footer;
} = ({
  children,
  userId,
  token,
  questId,
  onCompleteAllStatus,
  iconUrls = [],
  uniqueUserId,
  headingText,
  descriptionText,
  uniqueEmailId,
  autoHide = true,
  template = 1,
  showLoadingIndicator = true,
  showAnnouncement = false,
  ButtonType = "Arrow",
  allowMultiClick = false,
  onLinkTrigger = (url: string) => {
    window.location.href = url;
  },
  styleConfig,
  isImageOpen = false,
  onLoad,
  variation,
  Headers,
  isProgressNumber,
  accent,
  entityAuthenticationToken,
  isV1Api,
  customLoader,
  onClose,
}) => {
    const [formdata, setFormdata] = useState<TutorialStep[]>([]);
    const [showLoader, setShowLoader] = useState<boolean>(false);
    const [allActionCompleted, setAllActionCompleted] = useState<boolean>(false);
    const [criteriaSubmit, setCriteriaSubmit] = useState<string[]>([]);
    const [dropdowns, setDropdown] = useState<Array<boolean>>([]);
    const [campaignVariationId, setCampaignVariationId] = useState("");
    const [headers, setHeaders] = useState([]);
    const [showFooter, setShowFooter] = useState(isV1Api ? false : true);
    const [isVerifiedCalled, setIsVerifiedCalled] = useState(false);

    const { apiKey, entityId, apiType, themeConfig, user } = useContext(
      QuestContext.Context
    );
    const general = useGeneral('quest')

    const getStartedApi = new GetStartedApi(apiType);
    const GeneralFunctions = new General("mixpanel", apiType);

    const [themeManager, setThemeManager] = useState(
      () => new ThemeManager(themeConfig, styleConfig)
    );

    const completedPercentage = !isProgressNumber
      ? (formdata?.reduce((a, b) => a + (b.completed ? 1 : 0), 0) * 100) /
      formdata.length
      : formdata.filter((item) => item.completed).length;

    useEffect(() => {
      setThemeManager(new ThemeManager(themeConfig, styleConfig));
    }, [themeConfig, styleConfig]);

    const handleCriteriaClick = async (
      data: any,
      url: string
    ): Promise<void | boolean> => {
      if (!isV1Api) {
        general.trackEvent(
          'quest-get-started-criteria-clicked',
          (uniqueUserId || userId || user.userId)!,
          { data, url },
          true,
          !uniqueUserId
        )
      }

      if (url?.indexOf("walkthrough:") === 0) {
        const result = onLinkTrigger(url, data.actionId);
        if (result) return result;
        return;
      }

      if (showAnnouncement) {
        onLinkTrigger(url, data.actionId);
        return;
      }

      try {
        const userAuth = await getStartedApi.getUserAuth({
          apiKey,
          entityId,
          uniqueUserId,
          uniqueEmailId,
          userId,
          token,
          entityAuthenticationToken
        });

        if (!userAuth) return;

        let res = await getStartedApi.verifyAction(
          entityId,
          questId,
          apiKey,
          userAuth,
          data,
          campaignVariationId,
          isV1Api
        );

        if (isV1Api) {
          if (data?.type === "DAPP_METRIC_THRESHOLD" || data?.type === "MULTIPLE_METRICS" || !data?.url) {
            onLinkTrigger(url, data.actionId);
            if (res?.message === "Verify other criterias of the quest" || (res?.success && !res?.message)) {
              fetchData();
            }
            return;
          }
        }
        if (res?.message === "Verify other criterias of the quest") {
          fetchData();
        }
        setCriteriaSubmit([...criteriaSubmit, data.actionId]);
        onLinkTrigger(url, data.actionId);
      } catch (error) {
        GeneralFunctions.captureSentryException(error);
      }
    };

    const handleAutoVerify = async (data: any[]) => {
      if (!data || data.length === 0) return;
      try {
        let isRefreshData = false;
        const userAuth = await getStartedApi.getUserAuth({
          apiKey,
          entityId,
          uniqueUserId,
          uniqueEmailId,
          userId,
          token,
          entityAuthenticationToken
        });

        if (!userAuth) return;
        let filtered = data.filter((el: any) => (el.type === "DAPP_METRIC_THRESHOLD" || el.type === "MULTIPLE_METRICS") && el?.counter >= el?.threshold && el?.completed === false);
        try {
          const res = await getStartedApi.verifyAction(
            entityId,
            questId,
            apiKey,
            userAuth,
            filtered,
            campaignVariationId,
            isV1Api
          );
          if (res?.message === "Verify other criterias of the quest") {
            isRefreshData = true;
          }
        } catch (err) {
          GeneralFunctions.captureSentryException(err);
        }

        if (isRefreshData) {
          await fetchData();
        }
      } catch (error) {
        GeneralFunctions.captureSentryException(error);
      }
    };

    const fetchData = async () => {
      if (!entityId || !apiKey) return;
      setShowLoader(true);
      try {
        const userAuth = await getStartedApi.getUserAuth({
          apiKey,
          entityId,
          uniqueUserId,
          uniqueEmailId,
          userId,
          token,
          entityAuthenticationToken
        });

        if (!userAuth) return;

        const response = await getStartedApi.fetchCampaignData(
          entityId,
          questId,
          apiKey,
          userAuth,
          variation,
          isV1Api
        );

        if (isV1Api) {
          setShowFooter(false);
          const processedActions = response?.eligibilityData?.map((el: any) => {
            return {
              type: el?.data?.criteriaType,
              title: el?.data?.metadata?.linkActionName || el?.data?.metadata?.title,
              url: el?.data?.metadata?.linkActionUrl || el?.data?.metadata?.metadata?.deeplink,
              description: el?.data?.metadata?.description || "",
              btn1: el?.data?.metadata?.secondaryTitle || "",
              btn2: el?.data?.metadata?.buttonTitle,
              btn1Link: el?.data?.metadata?.secondaryLink,
              actionId: el?.data?.criteriaId,
              completed: el?.completed,
              longDescription: el?.data?.metadata?.description || "Default description",
              imageUrl: el?.data?.metadata?.imageUrl,
              counter: el?.progressData,
              threshold: Number(el?.data?.metadata?.metricCount)
            }
          })
          setFormdata(processedActions);
          const allCompleted = processedActions.every(
            (action: any) => action.completed
          );
          setAllActionCompleted(allCompleted);
        } else {
          if (response.data?.sdkConfig?.uiProps?.questThemeData) {
            setThemeManager((prev) => {
              prev.updateQuestTheme(
                response.data.sdkConfig.uiProps.questThemeData
              );
              if (response.data.sdkConfig.uiProps.BrandTheme) {
                prev.updateBrandTheme(
                  response.data.sdkConfig.uiProps.BrandTheme.properties
                );
              }
              return prev;
            });
          }
          if (response?.data?.sdkConfig?.uiProps?.showFooter !== undefined) {
            setShowFooter(response.data.sdkConfig.uiProps.showFooter);
          }

          if (
            accent &&
            !response?.data?.sdkConfig?.uiProps?.questThemeData?.accentColor
          ) {
            setThemeManager((prev) => {
              prev.updateQuestTheme({
                ...prev.questThemeData,
                accentColor: prev?.questThemeData?.accentColor
                  ? prev?.questThemeData?.accentColor
                  : accent,
              });

              return prev;
            });
          }

          if (response?.data?.sdkConfig?.uiProps?.Headers && response?.data?.sdkConfig?.uiProps?.Headers.length > 0) {
            setHeaders(response?.data?.sdkConfig?.uiProps?.Headers)
          }

          setCampaignVariationId(response.data.campaignVariationId);

          const processedActions = response.data.actions
            .map((action: any) => ({
              type: action.actionType,
              title: action.title,
              url: action.metadata?.link,
              description: action.description,
              btn1: action.metadata?.secondaryTitle || "",
              btn2: action.metadata?.buttonTitle,
              btn1Link: action.metadata?.secondaryLink,
              actionId: action.actionId,
              completed: action.isCompleted,
              longDescription: action.description || "Default description",
              imageUrl: action.metadata?.imageUrl,
            }));

          setFormdata(processedActions);

          const allCompleted = processedActions.every(
            (action: any) => action.completed
          );
          setAllActionCompleted(allCompleted);

          if (!dropdowns.length) {
            setDropdown(new Array(processedActions.length).fill(false));
          }
        }
      } catch (error) {
        GeneralFunctions.captureSentryException(error);
      } finally {
        setShowLoader(false);
      }
    };

    // useEffect(() => {
    //   if (isV1Api && formdata.length > 0 && !isVerifiedCalled) {
    //     handleAutoVerify(formdata);
    //     setIsVerifiedCalled(true);
    //   }
    // }, [formdata])

    useEffect(() => {
      GeneralFunctions.fireTrackingEvent(
        "quest_get_started_loaded",
        "get_started"
      );
      general.trackEvent(
        'quest-get-started-loaded',
        (uniqueUserId || userId || user.userId)!,
        {},
        true,
        !uniqueUserId,
        true
      )

      fetchData();
    }, [criteriaSubmit, uniqueUserId]);

    const refreshGetstarted = useCallback(async () => {
      await fetchData();
    }, []);

    useEffect(() => {
      if (!(window as any).refreshGetstarted) {
        (window as any).refreshGetstarted = refreshGetstarted;
      }

      return () => {
        if ((window as any).refreshGetstarted === refreshGetstarted) {
          delete (window as any).refreshGetstarted;
        }
      };
    }, [questId, refreshGetstarted]);

    useEffect(() => {
      if (allActionCompleted) {
        const claimReward = async () => {
          try {
            setShowLoader(true);
            const userAuth = await getStartedApi.getUserAuth({
              apiKey,
              entityId,
              uniqueUserId,
              uniqueEmailId,
              userId,
              token,
              entityAuthenticationToken
            });

            if (!userAuth) return;

            await getStartedApi.claimReward(
              entityId,
              questId,
              apiKey,
              userAuth,
              campaignVariationId,
              isV1Api
            );

            onCompleteAllStatus?.();
          } catch (error) {
            GeneralFunctions.captureSentryException(error);
          } finally {
            setShowLoader(false);
          }
        };

        claimReward();
      }
    }, [allActionCompleted, uniqueUserId]);

    useEffect(() => {
      if (formdata.length > 0) onLoad?.();
    }, [formdata]);

    const renderChildren = () => {
      if (!children) {
        return (
          <>
            <Header
              heading={headingText}
              subHeading={descriptionText}
              Headers={Headers || headers}
            />
            <Progress isProgressNumber={isProgressNumber} />
            <Content />
            {showFooter && <Footer />}
          </>
        );
      }

      return Children.map(children, (child) => {
        if (!React.isValidElement(child)) return null;

        if (child.type === Header) {
          return React.cloneElement(child, {
            heading: headingText,
            subHeading: descriptionText,
            Headers: Headers || headers,
            ...child.props,
          });
        }

        return child;
      });
    };

    const contextValue = {
      formdata,
      showLoader,
      allActionCompleted,
      completedPercentage,
      themeManager,
      handleCriteriaClick,
      dropdowns,
      setDropdown,
      template,
      isImageOpen,
      iconUrls,
      ButtonType,
      allowMultiClick,
      onClose
    };

    if (showLoadingIndicator && showLoader) return customLoader || <Loader />;

    return (
      <GetStartedContext.Provider value={contextValue}>
        {formdata.length > 0 && (
          <div className="get_started_box" style={themeManager.getFormStyles()}>
            {(autoHide ? !!formdata.length && !allActionCompleted : true) &&
              renderChildren()}
          </div>
        )}
      </GetStartedContext.Provider>
    );
  };

GetStarted.Header = Header;
GetStarted.Progress = Progress;
GetStarted.Content = Content;
GetStarted.Footer = Footer;

export default GetStarted;
